import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check, Moon, Sun, Sunrise, Clock, User, Users, Baby, Sparkles, PawPrint, Star, Zap, Film, Smile, Headphones, BookOpen, Wand2, SkipBack, Play, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBooks } from '@/hooks/use-books';
import { useBookAvatars } from '@/hooks/use-book-avatars';
import { supabase } from '@/integrations/supabase/client';
import { Book } from '@/types/book';
import { OnboardingPaymentSheet } from '@/components/onboarding/OnboardingPaymentSheet';
import { useReadyPaymentRequest as usePreloadedPaymentRequest } from '@/hooks/use-ready-payment-request';
import { toast } from '@/hooks/use-toast';
import { buildOnboardingCompletedPayload } from '@/utils/marketing';


type Step = 
  | 'library' | 'more' | 'reading-time' | 'favourite-film' | 'personalized-story'
  | 'custom-offer' | 'film-alternatives' | 'hero-confirm' | 'character-select' | 'production-started' | 'custom-paywall'
  | 'pick-hook' | 'reading-mode' | 'story-length' | 'custom-confirm'
  | 'who-for' | 'profile' | 'avatar' | 'profile-confirm' | 'interests' | 'goal' | 'building-library' | 'app-preview' | 'reader-preview'
  | 'review-showcase' | 'reading-journey';

interface FilmHook {
  id: string;
  title: string;
  description: string;
}

interface FilmCharacter {
  id: string;
  name: string;
  description: string;
}

interface AlternativeFilm {
  filmName: string;
  tagline: string;
  hooks: FilmHook[];
  characters: FilmCharacter[];
}

interface OnboardingState {
  selectedBooks: number[];
  moreBooks: number[];
  readingTime: string;
  favouriteFilm: string;
  // Personalized story
  heroName: string;
  generatedStory: string;
  // Film customization flow
  filmMatchFound: boolean | null;
  filmHooks: FilmHook[];
  filmCharacters: FilmCharacter[];
  selectedHook: string;
  selectedCharacter: string;
  readingMode: 'listen' | 'read' | 'both';
  storyLength: 'short' | 'standard' | 'epic';
  wantsCustomBook: boolean;
  // Film availability
  filmTaken: boolean;
  takenReason: 'already_book' | 'already_requested' | null;
  alternativeFilms: AlternativeFilm[];
  originalFilmName: string; // Store original if user picks alternative
  // Profile
  isForSelf: boolean | null;
  profileName: string;
  decadeKid: string;
  ageRange: string;
  avatarUrl: string;
  childAvatarUrl: string;
  interests: string[];
  readingGoal: number;
  addChildProfile: boolean;
  selectingChildAvatar: boolean;
}

const READING_TIMES = [
  { id: 'before-bed', label: 'Before Bed', icon: Moon },
  { id: 'quiet-afternoons', label: 'Quiet Afternoons', icon: Sun },
  { id: 'morning-routine', label: 'Morning Routine', icon: Sunrise },
  { id: 'anytime', label: 'Anytime they ask', icon: Clock },
];

const DECADES = ['60s', '80s', '90s', '2000s'];
const AGE_RANGES = ['3-5', '6-8', '9-12+'];

const INTERESTS = [
  { id: 'imagination', label: 'Big Imagination', icon: Sparkles },
  { id: 'animals', label: 'Animals', icon: PawPrint },
  { id: 'space', label: 'Space and Stars', icon: Star },
  { id: 'brave', label: 'Brave Moments', icon: Zap },
  { id: 'movies', label: 'Movie Time', icon: Film },
  { id: 'funny', label: 'Funny Stories', icon: Smile },
];

const READING_GOALS = [1, 2, 3, 4, 5];

const BUILDING_MESSAGES = [
  { text: "Curating your collection...", duration: 800 },
  { text: "Adding your favorites...", duration: 800 },
  { text: "Organizing your library...", duration: 800 },
  { text: "Almost ready...", duration: 600 },
];

interface BuildingLibraryStepProps {
  displayBooks: Book[];
  avatarUrl: string;
  profileName: string;
  onComplete: () => void;
}

function BuildingLibraryStep({ displayBooks, avatarUrl, profileName, onComplete }: BuildingLibraryStepProps) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [booksVisible, setBooksVisible] = useState<number[]>([]);
  const [arranged, setArranged] = useState(false);

  // Cycle through messages
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    let elapsed = 0;
    
    BUILDING_MESSAGES.forEach((msg, idx) => {
      if (idx > 0) {
        timers.push(setTimeout(() => setMessageIndex(idx), elapsed));
      }
      elapsed += msg.duration;
    });

    return () => timers.forEach(t => clearTimeout(t));
  }, []);

  // Stagger book reveals
  useEffect(() => {
    const bookIds = displayBooks.slice(0, 8).map(b => b.id);
    const timers: NodeJS.Timeout[] = [];
    
    bookIds.forEach((id, idx) => {
      timers.push(setTimeout(() => {
        setBooksVisible(prev => [...prev, id]);
      }, 400 + idx * 200));
    });

    // Trigger "arranged" state after books appear
    timers.push(setTimeout(() => setArranged(true), 400 + bookIds.length * 200 + 300));

    // Auto-advance after full animation
    timers.push(setTimeout(onComplete, 3500));

    return () => timers.forEach(t => clearTimeout(t));
  }, [displayBooks, onComplete]);

  // Random starting positions for books
  const getBookAnimation = (index: number) => {
    const positions = [
      { x: -150, y: -100, rotate: -25 },
      { x: 150, y: -80, rotate: 20 },
      { x: -120, y: 100, rotate: 15 },
      { x: 130, y: 120, rotate: -20 },
      { x: -80, y: -150, rotate: 30 },
      { x: 100, y: -130, rotate: -15 },
      { x: -100, y: 80, rotate: -30 },
      { x: 80, y: 150, rotate: 25 },
    ];
    return positions[index % positions.length];
  };

  const gridPositions = [
    { row: 0, col: 0 }, { row: 0, col: 1 }, { row: 0, col: 2 }, { row: 0, col: 3 },
    { row: 1, col: 0 }, { row: 1, col: 1 }, { row: 1, col: 2 }, { row: 1, col: 3 },
  ];

  return (
    <motion.div
      key="building-library"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex-1 flex flex-col items-center justify-center relative overflow-hidden"
    >
      {/* Avatar pulsing in corner */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="absolute top-8 right-8"
      >
        <motion.div
          animate={{ 
            boxShadow: ['0 0 0 0 rgba(var(--primary), 0.4)', '0 0 0 15px rgba(var(--primary), 0)', '0 0 0 0 rgba(var(--primary), 0)']
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-14 h-14 rounded-full overflow-hidden border-2 border-primary"
        >
          <img 
            src={avatarUrl || '/placeholder.svg'} 
            alt="Your avatar" 
            className="w-full h-full object-cover"
          />
        </motion.div>
      </motion.div>

      {/* Animated loading message */}
      <AnimatePresence mode="wait">
        <motion.h2
          key={messageIndex}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="text-xl sm:text-2xl font-semibold text-foreground text-center mb-8 z-10"
        >
          {BUILDING_MESSAGES[messageIndex].text}
        </motion.h2>
      </AnimatePresence>

      {/* For [Name] */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 0.5 }}
        className="text-foreground/60 text-sm mb-6 z-10"
      >
        For {profileName || 'you'}
      </motion.p>

      {/* Flying Books Animation Zone */}
      <div className="relative w-[280px] h-[200px]">
        {displayBooks.slice(0, 8).map((book, index) => {
          const startPos = getBookAnimation(index);
          const gridPos = gridPositions[index];
          const isVisible = booksVisible.includes(book.id);
          
          // Calculate final grid position (4 columns, 2 rows)
          const finalX = (gridPos.col - 1.5) * 65;
          const finalY = (gridPos.row - 0.5) * 95;
          
          return (
            <motion.div
              key={book.id}
              initial={{ 
                opacity: 0, 
                x: startPos.x, 
                y: startPos.y, 
                scale: 0.4,
                rotate: startPos.rotate 
              }}
              animate={isVisible ? { 
                opacity: 1, 
                x: arranged ? finalX : startPos.x * 0.3, 
                y: arranged ? finalY : startPos.y * 0.3, 
                scale: arranged ? 1 : 0.7,
                rotate: arranged ? 0 : startPos.rotate * 0.5
              } : {}}
              transition={{ 
                type: 'spring', 
                damping: arranged ? 15 : 25, 
                stiffness: arranged ? 200 : 300,
                duration: 0.6
              }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              style={{ zIndex: index }}
            >
              <motion.div
                animate={arranged ? { 
                  boxShadow: ['0 4px 20px rgba(0,0,0,0.2)', '0 4px 30px rgba(var(--primary), 0.3)', '0 4px 20px rgba(0,0,0,0.2)']
                } : {}}
                transition={{ duration: 1, repeat: arranged ? 1 : 0 }}
                className="w-14 aspect-[2/3] rounded-[2px] overflow-hidden shadow-xl"
              >
                <img 
                  src={book.cover_image_url || '/placeholder.svg'} 
                  alt={book.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Progress dots */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex gap-2 mt-10"
      >
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            animate={{ 
              scale: [1, 1.3, 1],
              opacity: [0.5, 1, 0.5]
            }}
            transition={{ 
              duration: 0.8, 
              repeat: Infinity,
              delay: i * 0.2
            }}
            className="w-2 h-2 rounded-full bg-primary"
          />
        ))}
      </motion.div>
    </motion.div>
  );
}

// Premium Reader Preview Step with auto-cycling page images
interface ReaderPreviewStepProps {
  displayBooks: Book[];
  remainingBooks: Book[];
  selectedBooks: number[];
  onContinue: () => void;
}

const STORY_SNIPPETS = [
  "The stars twinkled like diamonds scattered across velvet as Maya gazed up at the night sky.",
  "With a deep breath, Leo stepped through the ancient doorway, ready for whatever lay beyond.",
  "The forest whispered secrets only the bravest hearts could hear.",
  "She knew in that moment, everything was about to change forever.",
];

function ReaderPreviewStep({ displayBooks, remainingBooks, selectedBooks, onContinue }: ReaderPreviewStepProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  
  // Collect page images from all books
  const allPageImages = useMemo(() => {
    const images: { url: string; bookTitle: string }[] = [];
    const allBooksToCheck = [...displayBooks, ...remainingBooks];
    
    for (const book of allBooksToCheck) {
      // page_images comes from the database but isn't typed in the Book interface
      const pageImages = (book as any).page_images as Array<{ pageName: string; url: string }> | null;
      if (pageImages && Array.isArray(pageImages)) {
        const sortedPages = [...pageImages]
          .filter(p => p.pageName?.startsWith('page-'))
          .sort((a, b) => {
            const numA = parseInt(a.pageName?.replace('page-', '') || '0');
            const numB = parseInt(b.pageName?.replace('page-', '') || '0');
            return numA - numB;
          })
          .slice(0, 3); // Take first 3 pages from each book
        
        for (const page of sortedPages) {
          images.push({ url: page.url, bookTitle: book.title });
        }
      }
    }
    
    // Fallback to cover images if no page images
    if (images.length === 0) {
      for (const book of allBooksToCheck.slice(0, 6)) {
        if (book.cover_image_url) {
          images.push({ url: book.cover_image_url, bookTitle: book.title });
        }
      }
    }
    
    return images.slice(0, 8); // Limit to 8 images
  }, [displayBooks, remainingBooks]);
  
  // Auto-cycle through images
  useEffect(() => {
    if (allPageImages.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex(prev => (prev + 1) % allPageImages.length);
    }, 3500);
    
    return () => clearInterval(interval);
  }, [allPageImages.length]);

  // Auto-cycle through text snippets (swipe up effect)
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex(prev => (prev + 1) % STORY_SNIPPETS.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);

  const stepVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <motion.div
      key="reader-preview"
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="flex-1 flex flex-col items-center justify-center px-6 py-8 overflow-y-auto"
    >
      <motion.h1 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-2xl sm:text-3xl font-bold text-foreground text-center mb-2"
      >
        Our Custom Reader ✨
      </motion.h1>
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/70 text-center text-sm mb-6"
      >
        Audio narration • Auto-play • Beautiful illustrations
      </motion.p>

      {/* Phone Mockup with Reader - No float, static */}
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ 
          opacity: { delay: 0.3, duration: 0.5 },
          scale: { delay: 0.3, type: 'spring', damping: 20 },
          y: { delay: 0.3, type: 'spring', damping: 20 }
        }}
        className="relative w-[240px] h-[480px]"
      >
        {/* Animated glow behind phone - more dramatic */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: [0.4, 0.7, 0.4], 
            scale: [0.95, 1.08, 0.95],
            rotate: [0, 3, 0, -3, 0]
          }}
          transition={{ delay: 0.5, duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -inset-10 bg-gradient-to-br from-primary/50 via-purple-500/40 to-primary/50 rounded-[50px] blur-3xl -z-10"
        />

        {/* Phone Frame */}
        <div 
          className="relative w-full h-full rounded-[32px] p-2 shadow-2xl"
          style={{
            background: 'linear-gradient(180deg, #1a1a1a 0%, #0a0a0a 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
            border: '3px solid rgba(255, 255, 255, 0.15)',
          }}
        >
          {/* Notch */}
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-20" />
          
          {/* Screen */}
          <div className="w-full h-full bg-background rounded-[24px] overflow-hidden relative">
            {/* Auto-cycling Image Section with crossfade */}
            <div className="h-[45%] w-full relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentImageIndex}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ 
                    opacity: 1, 
                    scale: [1.05, 1],
                  }}
                  exit={{ opacity: 0 }}
                  transition={{ 
                    opacity: { duration: 0.8 },
                    scale: { duration: 3.5, ease: "easeOut" }
                  }}
                  className="absolute inset-0"
                >
                  <img 
                    src={allPageImages[currentImageIndex]?.url || '/placeholder.svg'}
                    alt="Book page"
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              </AnimatePresence>
              
              {/* Gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
              
              {/* Feature Callout - Illustrations */}
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 3.5, type: 'spring', damping: 15 }}
                className="absolute top-3 left-3 right-3"
              >
                <div className="bg-black/50 backdrop-blur-md border border-white/20 rounded-lg px-2 py-1.5 shadow-lg">
                  <p className="text-[9px] font-medium text-white text-center">🎨 Beautiful illustrations</p>
                </div>
                <motion.div 
                  initial={{ y: -3 }}
                  animate={{ y: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-black/50"
                />
              </motion.div>
              
              {/* Page indicator dots */}
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                {allPageImages.slice(0, 5).map((_, i) => (
                  <motion.div
                    key={i}
                    animate={{ 
                      scale: i === currentImageIndex % 5 ? 1.3 : 1,
                      opacity: i === currentImageIndex % 5 ? 1 : 0.4
                    }}
                    className="w-1 h-1 rounded-full bg-white"
                  />
                ))}
              </div>
              
              {/* Sparkle effects */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ delay: 3.5, duration: 1.5, repeat: Infinity, repeatDelay: 2 }}
                className="absolute top-12 right-4 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_4px_rgba(255,255,255,0.6)]"
              />
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 0] }}
                transition={{ delay: 4, duration: 1.5, repeat: Infinity, repeatDelay: 2.5 }}
                className="absolute top-16 left-6 w-1.5 h-1.5 bg-white rounded-full shadow-[0_0_8px_3px_rgba(255,255,255,0.5)]"
              />
            </div>

            {/* Text Content with swipe-up page turn effect */}
            <div className="px-4 py-3 h-[60px] overflow-hidden relative">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentTextIndex}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="text-[10px] leading-relaxed text-foreground/90"
                >
                  {STORY_SNIPPETS[currentTextIndex]}
                </motion.p>
              </AnimatePresence>
            </div>

            {/* Audio Player Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="absolute bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border/30 px-3 py-3"
            >
              {/* Feature Callout - Play Button */}
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.8 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 1.5, type: 'spring', damping: 15 }}
                className="absolute -top-10 left-2 right-2"
              >
                <div className="bg-black/50 backdrop-blur-md border border-white/20 rounded-lg px-2 py-1.5 shadow-lg">
                  <p className="text-[9px] font-medium text-white text-center">🎧 Auto-read stories aloud</p>
                </div>
                <motion.div 
                  initial={{ y: -3 }}
                  animate={{ y: [0, 3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-black/50"
                />
              </motion.div>

              {/* Progress Bar - Animated */}
              <div className="mb-2">
                <div className="h-1 bg-muted rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: '20%' }}
                    animate={{ width: ['20%', '55%', '20%'] }}
                    transition={{ delay: 1.2, duration: 8, repeat: Infinity, ease: "linear" }}
                    className="h-full bg-primary rounded-full"
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <motion.span 
                    className="text-[8px] text-muted-foreground"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2 }}
                  >
                    0:45
                  </motion.span>
                  <span className="text-[8px] text-muted-foreground">2:15</span>
                </div>
              </div>

              {/* Controls with Autoplay */}
              <div className="flex items-center justify-between">
                <div className="w-12" />
                
                <div className="flex items-center justify-center gap-4">
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="w-6 h-6 flex items-center justify-center opacity-70">
                    <SkipBack className="w-4 h-4 text-foreground fill-foreground" />
                  </motion.button>
                  
                  {/* Play button with pulse animation */}
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 1.0, type: 'spring', damping: 15 }}
                    className="relative w-10 h-10 bg-primary rounded-full flex items-center justify-center shadow-lg"
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 1 }}
                      animate={{ opacity: [0.6, 0], scale: [1, 1.5] }}
                      transition={{ delay: 1.5, duration: 1.5, repeat: Infinity }}
                      className="absolute inset-0 bg-primary rounded-full"
                    />
                    <Play className="w-4 h-4 ml-0.5 relative z-10 text-primary-foreground fill-primary-foreground" />
                  </motion.button>
                  
                  <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }} className="w-6 h-6 flex items-center justify-center opacity-70">
                    <SkipForward className="w-4 h-4 text-foreground fill-foreground" />
                  </motion.button>
                </div>
                
                {/* Autoplay toggle */}
                <div className="relative">
                  <motion.div
                    initial={{ opacity: 0, x: 10, scale: 0.8 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    transition={{ delay: 2.5, type: 'spring', damping: 15 }}
                    className="absolute -top-12 -left-14 w-20"
                  >
                    <div className="bg-black/50 backdrop-blur-md border border-white/20 rounded-lg px-1.5 py-1 shadow-lg">
                      <p className="text-[8px] font-medium text-white text-center">🙌 Hands-free</p>
                    </div>
                    <motion.div 
                      initial={{ y: -2 }}
                      animate={{ y: [0, 2, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="absolute right-2 -bottom-2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-black/50"
                    />
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 }}
                    className="flex flex-col items-center"
                  >
                    <motion.div
                      animate={{ 
                        boxShadow: ['0 0 0 0 hsl(var(--primary) / 0)', '0 0 12px 4px hsl(var(--primary) / 0.5)', '0 0 0 0 hsl(var(--primary) / 0)']
                      }}
                      transition={{ delay: 2.5, duration: 2, repeat: 3 }}
                      className="w-8 h-4 bg-primary rounded-full relative"
                    >
                      <motion.div 
                        initial={{ x: 0 }}
                        animate={{ x: 16 }}
                        transition={{ delay: 2.7, duration: 0.3 }}
                        className="absolute top-0.5 left-0.5 w-3 h-3 bg-white rounded-full shadow-sm"
                      />
                    </motion.div>
                    <span className="text-[7px] text-muted-foreground mt-0.5">Auto</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 4 }}
        className="pt-6 flex justify-center w-full"
      >
        <motion.div
          animate={{ scale: [1, 1.02, 1] }}
          transition={{ delay: 4.5, duration: 2, repeat: Infinity }}
        >
          <Button
            onClick={onContinue}
            className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
            style={{ borderRadius: '14px / 18px', minWidth: '200px' }}
          >
            Let's Go! ✨
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

// Review Showcase Step - scrolls to end, leaves review, shows IG-style reviews
interface ReviewShowcaseStepProps {
  displayBooks: Book[];
  onContinue: () => void;
}

function ReviewShowcaseStep({ displayBooks, onContinue }: ReviewShowcaseStepProps) {
  const [phase, setPhase] = useState<'scroll' | 'review-input' | 'review-posted' | 'reviews-wall'>('scroll');
  const book = displayBooks[0];
  
  useEffect(() => {
    // Phase 1: Scroll animation (1.5s)
    const timer1 = setTimeout(() => setPhase('review-input'), 1500);
    // Phase 2: Review input animation (2s)
    const timer2 = setTimeout(() => setPhase('review-posted'), 3500);
    // Phase 3: Show reviews wall (1.5s)
    const timer3 = setTimeout(() => setPhase('reviews-wall'), 5000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const fakeReviews = [
    { name: 'Sarah M.', avatar: '👩', rating: 5, text: 'My kids absolutely loved this! The audio is perfect 🎧', time: '2h ago', likes: 24 },
    { name: 'James K.', avatar: '👨', rating: 5, text: 'Beautiful illustrations, great bedtime stories ⭐', time: '5h ago', likes: 18 },
    { name: 'Emma L.', avatar: '👧', rating: 5, text: 'We read together every night now. Best app ever!', time: '1d ago', likes: 42 },
    { name: 'Michael R.', avatar: '👴', rating: 5, text: 'Perfect for my grandkids. They ask for it daily!', time: '2d ago', likes: 31 },
    { name: 'Lisa T.', avatar: '👩‍🦰', rating: 5, text: 'The auto-play feature is a game changer 🙌', time: '3d ago', likes: 15 },
  ];

  const stepVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <motion.div
      key="review-showcase"
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="flex-1 flex flex-col items-center px-4 py-4 w-full overflow-y-auto overscroll-none"
    >
      <motion.h1 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-bold text-foreground text-center mb-1 flex-shrink-0"
      >
        {phase === 'reviews-wall' ? 'Join Our Community 💬' : 'Share Your Journey'}
      </motion.h1>
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/70 text-center text-xs mb-3 flex-shrink-0"
      >
        {phase === 'reviews-wall' ? 'Thousands of families love BuxBox' : 'Leave reviews • Connect with readers'}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', damping: 20 }}
        className="relative w-[200px] h-[380px] sm:w-[240px] sm:h-[420px] flex-shrink-0"
      >

        {/* Phone frame - no notch */}
        <div 
          className="relative w-full h-full rounded-[24px] p-1.5 shadow-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            border: '2px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <div className="w-full h-full bg-background rounded-[20px] overflow-hidden relative">
            {/* Scrolling to end phase */}
            {phase === 'scroll' && (
              <motion.div
                initial={{ y: 0 }}
                animate={{ y: -200 }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
                className="absolute inset-0"
              >
                {/* Simulated content scrolling */}
                <div className="p-4 space-y-3">
                  {[...Array(8)].map((_, i) => (
                    <motion.div 
                      key={i}
                      className="h-12 bg-white/5 rounded-lg"
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.1 }}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* Review input phase */}
            {(phase === 'review-input' || phase === 'review-posted') && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex flex-col"
              >
                {/* Book header */}
                <div className="p-3 border-b border-white/10 flex items-center gap-3">
                  <img 
                    src={book?.cover_image_url || '/placeholder.svg'} 
                    alt="Book" 
                    className="w-10 h-14 rounded-md object-cover"
                  />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-foreground line-clamp-1">{book?.title || 'Amazing Story'}</p>
                    <p className="text-[10px] text-foreground/60">Leave a review</p>
                  </div>
                </div>

                {/* Stars */}
                <div className="p-4 flex justify-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <motion.div
                      key={star}
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.1 + star * 0.1, type: 'spring', damping: 10 }}
                    >
                      <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                    </motion.div>
                  ))}
                </div>

                {/* Review text area */}
                <div className="px-4">
                  <motion.div 
                    className="bg-white/5 border border-white/10 rounded-xl p-3"
                    initial={{ opacity: 0.5 }}
                    animate={{ opacity: 1 }}
                  >
                    <motion.p 
                      className="text-[11px] text-foreground"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.5 }}
                    >
                      {phase === 'review-posted' 
                        ? "Absolutely loved reading this with my kids! The illustrations are beautiful and the audio narration is perfect. ⭐⭐⭐⭐⭐"
                        : "Absolutely loved reading this with my k"
                      }
                      {phase === 'review-input' && (
                        <motion.span
                          animate={{ opacity: [1, 0] }}
                          transition={{ duration: 0.5, repeat: Infinity }}
                          className="inline-block w-0.5 h-3 bg-primary ml-0.5"
                        />
                      )}
                    </motion.p>
                  </motion.div>
                </div>

                {/* Submit button */}
                <div className="p-4">
                  <motion.button
                    initial={{ opacity: 0.5 }}
                    animate={{ 
                      opacity: phase === 'review-posted' ? 0.5 : 1,
                      scale: phase === 'review-posted' ? 0.98 : 1
                    }}
                    className="w-full py-2.5 bg-primary text-primary-foreground text-xs font-semibold rounded-xl flex items-center justify-center gap-2"
                  >
                    {phase === 'review-posted' ? (
                      <>
                        <Check className="w-3 h-3" />
                        Posted!
                      </>
                    ) : (
                      'Post Review'
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Reviews wall - Instagram style */}
            {phase === 'reviews-wall' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute inset-0 overflow-hidden"
              >
                <div className="p-3 border-b border-white/10 flex items-center justify-between">
                  <p className="text-xs font-bold text-foreground">Reviews</p>
                  <p className="text-[10px] text-primary">2,847 reviews</p>
                </div>
                
                <div className="p-2 space-y-2 overflow-y-auto max-h-[280px] sm:max-h-[320px]">
                  {fakeReviews.map((review, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.15 }}
                      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-3"
                    >
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-sm">
                          {review.avatar}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] font-semibold text-foreground">{review.name}</p>
                            <p className="text-[8px] text-foreground/40">{review.time}</p>
                          </div>
                          <div className="flex gap-0.5 my-0.5">
                            {[...Array(review.rating)].map((_, j) => (
                              <Star key={j} className="w-2 h-2 text-yellow-400 fill-yellow-400" />
                            ))}
                          </div>
                          <p className="text-[9px] text-foreground/80 leading-relaxed">{review.text}</p>
                          <div className="flex items-center gap-3 mt-1.5">
                            <motion.button 
                              whileTap={{ scale: 0.9 }}
                              className="flex items-center gap-1 text-[8px] text-foreground/50"
                            >
                              ❤️ {review.likes}
                            </motion.button>
                            <button className="text-[8px] text-foreground/50">Reply</button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: phase === 'reviews-wall' ? 1 : 0.3 }} 
        transition={{ delay: 0.5 }} 
        className="pt-4 pb-6 flex justify-center w-full flex-shrink-0"
      >
        <Button
          onClick={onContinue}
          disabled={phase !== 'reviews-wall'}
          className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
          style={{ borderRadius: '14px / 18px' }}
        >
          Continue
        </Button>
      </motion.div>
    </motion.div>
  );
}

// Reading Journey Showcase Step
interface ReadingJourneyStepProps {
  onContinue: () => void;
}

function ReadingJourneyStep({ onContinue }: ReadingJourneyStepProps) {
  const [showStats, setShowStats] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setShowStats(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const stats = [
    { icon: '📚', label: 'Books Read', value: '12', color: 'from-blue-500 to-cyan-500' },
    { icon: '⏱️', label: 'Reading Time', value: '8h 24m', color: 'from-purple-500 to-pink-500' },
    { icon: '🔥', label: 'Day Streak', value: '7', color: 'from-orange-500 to-red-500' },
    { icon: '🌙', label: 'Night Reads', value: '5', color: 'from-indigo-500 to-purple-500' },
  ];

  const journeyStories = [
    { gradient: 'from-orange-500 to-red-500', icon: '🔥', label: 'Streak' },
    { gradient: 'from-blue-500 to-cyan-500', icon: '📖', label: 'Books' },
    { gradient: 'from-green-500 to-emerald-500', icon: '🎯', label: 'Goal' },
    { gradient: 'from-purple-500 to-pink-500', icon: '📊', label: 'Stats' },
  ];

  const stepVariants = {
    enter: { opacity: 0, y: 20 },
    center: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <motion.div
      key="reading-journey"
      variants={stepVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={{ duration: 0.3 }}
      className="flex-1 flex flex-col items-center px-4 py-4 w-full overflow-y-auto overscroll-none"
    >
      <motion.h1 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-xl sm:text-2xl font-bold text-foreground text-center mb-1 flex-shrink-0"
      >
        Track Your Journey 📊
      </motion.h1>
      <motion.p 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-foreground/70 text-center text-xs mb-3 flex-shrink-0"
      >
        Beautiful stats • Reading streaks • Goals
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', damping: 20 }}
        className="relative w-[200px] h-[380px] sm:w-[240px] sm:h-[420px] flex-shrink-0"
      >

        {/* Phone frame - no notch */}
        <div
          className="relative w-full h-full rounded-[24px] p-1.5 shadow-2xl overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)',
            border: '2px solid rgba(255, 255, 255, 0.12)',
          }}
        >
          <div className="w-full h-full bg-background rounded-[20px] overflow-hidden relative p-2">
            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-center mb-3"
            >
              <p className="text-xs font-bold text-foreground">Your Reading Journey</p>
            </motion.div>

            {/* Story circles - Instagram style */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center gap-2 mb-2"
            >
              {journeyStories.map((story, i) => (
                <motion.div
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.6 + i * 0.1, type: 'spring', damping: 12 }}
                  className="flex flex-col items-center gap-0.5"
                >
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${story.gradient} p-0.5`}>
                    <div className="w-full h-full rounded-full bg-background flex items-center justify-center text-sm">
                      {story.icon}
                    </div>
                  </div>
                  <span className="text-[7px] text-foreground/60">{story.label}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-1.5 mb-2">
              {stats.map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={showStats ? { opacity: 1, scale: 1 } : {}}
                  transition={{ delay: i * 0.1, type: 'spring', damping: 15 }}
                  className="relative overflow-hidden rounded-lg p-2"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-10`}
                    animate={{ opacity: [0.05, 0.15, 0.05] }}
                    transition={{ duration: 3, repeat: Infinity, delay: i * 0.5 }}
                  />
                  <div className="relative z-10">
                    <span className="text-sm">{stat.icon}</span>
                    <motion.p 
                      className="text-base font-bold text-foreground"
                      initial={{ opacity: 0 }}
                      animate={showStats ? { opacity: 1 } : {}}
                      transition={{ delay: 0.3 + i * 0.1 }}
                    >
                      {stat.value}
                    </motion.p>
                    <p className="text-[8px] text-foreground/60">{stat.label}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Weekly chart */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={showStats ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.6 }}
              className="rounded-lg p-2"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
            >
              <p className="text-[9px] font-semibold text-foreground mb-1">This Week</p>
              <div className="flex items-end justify-between h-10 gap-0.5">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => {
                  const heights = [60, 40, 80, 55, 90, 30, 75];
                  return (
                    <div key={i} className="flex flex-col items-center gap-0.5 flex-1">
                      <motion.div
                        initial={{ height: 0 }}
                        animate={showStats ? { height: `${heights[i]}%` } : {}}
                        transition={{ delay: 0.8 + i * 0.05, type: 'spring', damping: 15 }}
                        className="w-full bg-gradient-to-t from-primary to-primary/60 rounded-sm"
                      />
                      <span className="text-[6px] text-foreground/40">{day}</span>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Reading goal */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={showStats ? { opacity: 1 } : {}}
              transition={{ delay: 1 }}
              className="mt-2 flex items-center gap-2 px-1"
            >
              <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={showStats ? { width: '70%' } : {}}
                  transition={{ delay: 1.2, duration: 0.8, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                />
              </div>
              <span className="text-[9px] font-medium text-foreground">7/10</span>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={showStats ? { opacity: 1 } : {}}
              transition={{ delay: 1.3 }}
              className="text-[7px] text-foreground/60 text-center mt-1"
            >
              Monthly Goal Progress 🎯
            </motion.p>
          </div>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ delay: 1.5 }} 
        className="pt-4 pb-6 flex justify-center w-full flex-shrink-0"
      >
        <motion.div animate={{ scale: [1, 1.02, 1] }} transition={{ delay: 2, duration: 2, repeat: Infinity }}>
          <Button
            onClick={onContinue}
            className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
            style={{ borderRadius: '14px / 18px', minWidth: '200px' }}
          >
            Get Started ✨
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

export default function LetsBeginGo() {
  const navigate = useNavigate();
  const { allBooks, isLoading } = useBooks();
  const { avatars } = useBookAvatars();
  
  // Pre-load payment request for Apple/Google Pay - starts loading immediately
  usePreloadedPaymentRequest();
  
  const [step, setStep] = useState<Step>('library');
  const [state, setState] = useState<OnboardingState>({
    selectedBooks: [],
    moreBooks: [],
    readingTime: '',
    favouriteFilm: '',
    // Personalized story
    heroName: '',
    generatedStory: '',
    // Film customization
    filmMatchFound: null,
    filmHooks: [],
    filmCharacters: [],
    selectedHook: '',
    selectedCharacter: '',
    readingMode: 'both',
    storyLength: 'standard',
    wantsCustomBook: false,
    // Film availability
    filmTaken: false,
    takenReason: null,
    alternativeFilms: [],
    originalFilmName: '',
    // Profile
    isForSelf: null,
    profileName: '',
    decadeKid: '',
    ageRange: '',
    avatarUrl: '',
    childAvatarUrl: '',
    interests: [],
    readingGoal: 3,
    addChildProfile: false,
    selectingChildAvatar: false,
  });
  const [isAnalyzingFilm, setIsAnalyzingFilm] = useState(false);
  const [filmAnalysisComplete, setFilmAnalysisComplete] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  
  // Personalized story generation state
  const [isGeneratingStory, setIsGeneratingStory] = useState(false);
  const [displayedStoryText, setDisplayedStoryText] = useState('');
  const [storyStage, setStoryStage] = useState<'generating' | 'story'>('generating');


  // Generate session token for anonymous tracking
  const [sessionToken] = useState(() => {
    const existing = localStorage.getItem('onboarding_session_token');
    if (existing) return existing;
    const token = crypto.randomUUID();
    localStorage.setItem('onboarding_session_token', token);
    return token;
  });

  // Get books for display (exclude preview books, limit to 6)
  const displayBooks = allBooks?.filter(b => !b.is_preview).slice(0, 6) || [];
  
  // For "more like this" step, show different books than step 1 (exclude all displayBooks, not just selected)
  const displayBookIds = displayBooks.map(b => b.id);
  const remainingBooks = allBooks?.filter(
    b => !b.is_preview && !displayBookIds.includes(b.id)
  ).slice(0, 6) || [];

  // Randomize avatars for selection
  const randomizedAvatars = useMemo(() => {
    if (!avatars || avatars.length === 0) return [];
    return [...avatars].sort(() => Math.random() - 0.5).slice(0, 12);
  }, [avatars]);

  // Build steps array dynamically - custom film steps come at the END after reader-preview
  const getSteps = useCallback((): Step[] => {
    const baseSteps: Step[] = [
      'library', 'more', 'reading-time', 'favourite-film',
      'who-for', 'profile', 'avatar', 'profile-confirm', 'interests', 'goal', 
      'building-library', 'app-preview', 'reader-preview', 'review-showcase', 'reading-journey'
    ];
    
    // Add custom-offer if user entered a favourite film (always show it for back navigation)
    if (state.favouriteFilm.trim()) {
      baseSteps.push('custom-offer');
      
      // Only add remaining steps if user wants custom book
      if (state.wantsCustomBook) {
        baseSteps.push('hero-confirm', 'character-select', 'pick-hook', 'personalized-story', 'production-started', 'custom-paywall');
      }
    }
    
    return baseSteps;
  }, [state.favouriteFilm, state.filmHooks.length, state.wantsCustomBook]);

  const steps = getSteps();
  const currentStepIndex = steps.indexOf(step);

  // Analyze film with AI - runs in background while user continues
  const analyzeFilmInBackground = useCallback(async (filmName: string) => {
    if (!filmName.trim()) return;
    
    setIsAnalyzingFilm(true);
    try {
      // Get user ID to check if they already own this request
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase.functions.invoke('analyse-film', {
        body: { 
          filmName: filmName,
          heroName: null, // Will be updated later when we know the profile name
          userId: user?.id || null
        }
      });
      
      if (error) {
        console.error('Error analyzing film:', error);
        setState(prev => ({ ...prev, filmMatchFound: true }));
        setFilmAnalysisComplete(true);
        return;
      }
      
      console.log('Film analysis result:', data);
      
      // Check if film is taken and we have alternatives
      if (data.isTaken && data.alternatives && data.alternatives.length > 0) {
        console.log('Film is taken, got alternatives:', data.alternatives);
        setState(prev => ({ 
          ...prev, 
          filmTaken: true,
          takenReason: data.takenReason || 'already_requested',
          alternativeFilms: data.alternatives,
          originalFilmName: data.filmName || filmName,
          filmMatchFound: false, // We found the film, it's just taken
        }));
      } else if (data.isValidFilm && data.hooks && data.hooks.length > 0) {
        // Film recognized and available - store hooks and characters for later
        setState(prev => ({ 
          ...prev, 
          filmMatchFound: false, // Not in library, offer to create
          filmTaken: false,
          filmHooks: data.hooks,
          filmCharacters: data.characters || [],
          favouriteFilm: data.filmName || prev.favouriteFilm
        }));
      } else {
        // Film not recognized
        setState(prev => ({ ...prev, filmMatchFound: true }));
      }
    } catch (err) {
      console.error('Film analysis error:', err);
      setState(prev => ({ ...prev, filmMatchFound: true }));
    } finally {
      setIsAnalyzingFilm(false);
      setFilmAnalysisComplete(true);
    }
  }, []);

  // Generate personalized story with AI
  const generatePersonalizedStory = useCallback(async () => {
    const heroName = state.profileName || 'the hero';
    if (!state.favouriteFilm.trim()) return;
    
    setStoryStage('generating');
    setIsGeneratingStory(true);
    
    try {
      // Get the selected hook description for scene mood
      const selectedHookData = state.filmHooks.find(h => h.id === state.selectedHook);
      const sceneMood = selectedHookData?.description || null;
      
      const { data, error } = await supabase.functions.invoke('generate-personalized-story', {
        body: {
          heroName: heroName,
          filmName: state.favouriteFilm,
          sceneMood: sceneMood
        }
      });
      
      if (error) {
        console.error('Story generation error:', error);
        toast({
          title: "Couldn't generate story",
          description: "Please try again",
          variant: "destructive"
        });
        // Skip to next step on error
        setStep('production-started');
        return;
      }
      
      const story = data?.story || '';
      setState(prev => ({ ...prev, generatedStory: story }));
      setStoryStage('story');
      
      // Typewriter effect
      setDisplayedStoryText('');
      let currentIndex = 0;
      const typeInterval = setInterval(() => {
        if (currentIndex < story.length) {
          setDisplayedStoryText(story.substring(0, currentIndex + 1));
          currentIndex++;
        } else {
          clearInterval(typeInterval);
        }
      }, 30);
      
    } catch (err) {
      console.error('Story generation error:', err);
      toast({
        title: "Couldn't generate story",
        description: "Please try again",
        variant: "destructive"
      });
      // Skip to next step on error
      setStep('production-started');
    } finally {
      setIsGeneratingStory(false);
    }
  }, [state.heroName, state.favouriteFilm]);

  const saveCustomBookRequest = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Use type assertion since the types may not be updated yet
      await (supabase.from('custom_book_requests') as any).insert({
        user_id: user?.id || null,
        session_token: sessionToken,
        film_name: state.favouriteFilm,
        hero_name: state.profileName || null,
        ai_hooks: state.filmHooks,
        selected_hook: state.selectedHook,
        selected_character: state.selectedCharacter,
        reading_mode: state.readingMode,
        story_length: state.storyLength,
        status: 'pending'
      });
      
      console.log('Custom book request saved');
    } catch (error) {
      console.error('Error saving custom book request:', error);
    }
  };

  const handleBack = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(steps[prevIndex]);
    } else {
      navigate('/letsbegin');
    }
  };

  const handleContinue = async () => {
    // When leaving favourite-film, start background analysis and continue to who-for
    if (step === 'favourite-film') {
      if (state.favouriteFilm.trim()) {
        // Start analysis in background
        analyzeFilmInBackground(state.favouriteFilm);
      }
      // Continue to who-for
      setStep('who-for');
      return;
    }
    
    // After pick-hook, skip story generation for now and go directly to production-started
    if (step === 'pick-hook') {
      setStep('production-started');
      return;
    }
    
    // After personalized story, continue to production-started (kept for future use)
    if (step === 'personalized-story') {
      setStep('production-started');
      return;
    }
    
    // After reader-preview, continue to review-showcase
    if (step === 'reader-preview') {
      setStep('review-showcase');
      return;
    }
    
    // After review-showcase, continue to reading-journey
    if (step === 'review-showcase') {
      setStep('reading-journey');
      return;
    }
    
    // After reading-journey, check if we should show custom offer
    if (step === 'reading-journey') {
      // If user entered a favourite film, always show custom offer
      if (state.favouriteFilm.trim()) {
        setStep('custom-offer');
        return;
      }
      // No film entered - complete onboarding
      await saveOnboardingData();
      navigate('/');
      return;
    }
    
    if (step === 'custom-offer') {
      if (state.wantsCustomBook) {
        setStep('hero-confirm');
      } else {
        // User declined - complete onboarding
        await saveOnboardingData();
        navigate('/');
      }
      return;
    }
    
    if (step === 'hero-confirm') {
      setStep('character-select');
      return;
    }
    
    if (step === 'character-select') {
      setStep('pick-hook');
      return;
    }
    
    if (step === 'production-started') {
      setStep('custom-paywall');
      return;
    }
    
    // custom-paywall is handled by its own buttons (Accept/Maybe Later)
    
    if (step === 'custom-confirm') {
      await saveCustomBookRequest();
      await saveOnboardingData();
      navigate('/');
      return;
    }
    
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setStep(steps[nextIndex]);
    } else {
      // Complete - save to database and navigate
      await saveOnboardingData();
      navigate('/');
    }
  };

  const saveOnboardingData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Save to onboarding_sessions table
      await supabase.from('onboarding_sessions').upsert({
        session_token: sessionToken,
        user_id: user?.id || null,
        selected_books: state.selectedBooks,
        more_books: state.moreBooks,
        reading_time: state.readingTime,
        favourite_film: state.favouriteFilm,
        is_for_self: state.isForSelf,
        profile_name: state.profileName,
        decade_kid: state.decadeKid,
        age_range: state.ageRange,
        avatar_url: state.avatarUrl,
        child_avatar_url: state.childAvatarUrl,
        interests: state.interests,
        reading_goal: state.readingGoal,
        add_child_profile: state.addChildProfile,
        completed_at: new Date().toISOString(),
      }, {
        onConflict: 'session_token',
      });

      // Create/update user profiles if logged in
      if (user?.id) {
        // Check if primary profile already exists
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('id, name, first_name, last_name, country')
          .eq('user_id', user.id)
          .eq('is_primary', true)
          .single();

        let primaryProfileId = existingProfile?.id;

        if (existingProfile) {
          // Update existing primary profile with onboarding data
          await supabase
            .from('user_profiles')
            .update({
              name: state.isForSelf ? state.profileName : existingProfile.id ? undefined : parentName,
              avatar_url: state.avatarUrl || undefined,
              profile_settings: {
                decade_kid: state.decadeKid,
                interests: state.interests,
                reading_goal: state.readingGoal,
                reading_time: state.readingTime,
                onboarding_completed: true,
                onboarding_completed_at: new Date().toISOString(),
              }
            })
            .eq('id', existingProfile.id);
        } else {
          // Create new primary profile
          const { data: newProfile, error: parentError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: user.id,
              name: state.isForSelf ? state.profileName : parentName,
              avatar_url: state.avatarUrl || null,
              profile_type: 'primary',
              display_order: 0,
              is_primary: true,
              profile_settings: {
                decade_kid: state.decadeKid,
                interests: state.interests,
                reading_goal: state.readingGoal,
                reading_time: state.readingTime,
                onboarding_completed: true,
                onboarding_completed_at: new Date().toISOString(),
              }
            })
            .select()
            .single();

          if (parentError) {
            console.error('Error creating parent profile:', parentError);
          } else if (newProfile) {
            primaryProfileId = newProfile.id;
            // Set as current profile
            await supabase
              .from('user_current_profile')
              .upsert({
                user_id: user.id,
                current_profile_id: newProfile.id,
              }, {
                onConflict: 'user_id',
              });
          }
        }

        // Create child profile if "for me and my kid" was selected OR addChildProfile is true
        if ((state.isForSelf === false || state.addChildProfile) && state.profileName) {
          const childName = state.profileName.trim();
          
          // Check if child profile already exists (check by name or profile_type 'kids')
          const { data: existingChild } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('user_id', user.id)
            .eq('name', childName)
            .single();

          if (!existingChild) {
            // Get the current max display_order for this user
            const { data: profiles } = await supabase
              .from('user_profiles')
              .select('display_order')
              .eq('user_id', user.id)
              .order('display_order', { ascending: false })
              .limit(1);
            
            const nextDisplayOrder = (profiles?.[0]?.display_order ?? 0) + 1;
            
            console.log('Creating child profile with name:', childName, 'display_order:', nextDisplayOrder);
            const { data: childData, error: childError } = await supabase
              .from('user_profiles')
              .insert({
                user_id: user.id,
                name: childName,
                avatar_url: state.childAvatarUrl || null,
                profile_type: 'kids',
                display_order: nextDisplayOrder,
                is_primary: false,
                profile_settings: {
                  age_range: state.ageRange,
                  interests: state.interests,
                  reading_goal: state.readingGoal,
                  reading_time: state.readingTime,
                }
              })
              .select()
              .single();

            if (childError) {
              console.error('Error creating child profile:', childError);
              toast({
                title: "Could not create child profile",
                description: childError.message,
                variant: "destructive"
              });
            } else {
              console.log('Child profile created successfully:', childData);
            }
          }
        }

        // Mark onboarding as complete
        localStorage.setItem('onboarding_completed', 'true');
        localStorage.setItem('onboarding_completed_user_id', user.id);
        
        // Fire onboarding_completed event to Zapier
        try {
          // Get book titles for selected books
          const selectedBookTitles = allBooks
            ?.filter(b => state.selectedBooks.includes(b.id))
            .map(b => b.title) || [];
          const moreBookTitles = allBooks
            ?.filter(b => state.moreBooks.includes(b.id))
            .map(b => b.title) || [];
          
          const payload = buildOnboardingCompletedPayload({
            user_id: user.id,
            email: user.email || '',
            first_name: existingProfile?.first_name || state.profileName || '',
            last_name: existingProfile?.last_name || '',
            country: existingProfile?.country || '',
            profile_id: primaryProfileId,
            reading_time: state.readingTime,
            favourite_film: state.favouriteFilm,
            interests: state.interests,
            decade_kid: state.decadeKid,
            age_range: state.ageRange,
            reading_goal: state.readingGoal,
            is_for_self: state.isForSelf,
            profile_name: state.profileName,
            add_child_profile: state.addChildProfile,
            selected_book_titles: selectedBookTitles,
            more_book_titles: moreBookTitles,
          });
          
          console.log('[LetsBeginGo] Sending onboarding_completed to Zapier:', payload);
          
          await supabase.functions.invoke('notify-signup', {
            body: payload
          });
        } catch (zapierError) {
          // Don't fail onboarding if Zapier webhook fails
          console.error('[LetsBeginGo] Zapier webhook error:', zapierError);
        }
      }

      // Also save to localStorage as backup
      localStorage.setItem('onboarding_preferences', JSON.stringify(state));
    } catch (error) {
      console.error('Error saving onboarding data:', error);
      // Still save to localStorage if DB fails
      localStorage.setItem('onboarding_preferences', JSON.stringify(state));
    }
  };

  const toggleBook = (bookId: number, isMoreStep: boolean) => {
    if (isMoreStep) {
      setState(prev => ({
        ...prev,
        moreBooks: prev.moreBooks.includes(bookId)
          ? prev.moreBooks.filter(id => id !== bookId)
          : [...prev.moreBooks, bookId],
      }));
    } else {
      setState(prev => ({
        ...prev,
        selectedBooks: prev.selectedBooks.includes(bookId)
          ? prev.selectedBooks.filter(id => id !== bookId)
          : [...prev.selectedBooks, bookId],
      }));
    }
  };

  const toggleInterest = (interestId: string) => {
    setState(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(id => id !== interestId)
        : [...prev.interests, interestId],
    }));
  };

  const canContinue = () => {
    switch (step) {
      case 'library':
        return state.selectedBooks.length >= 1;
      case 'more':
        return true; // Optional selection
      case 'reading-time':
        return state.readingTime !== '';
      case 'favourite-film':
        return state.favouriteFilm.trim() !== '';
      case 'custom-offer':
        return true; // They can choose yes or no
      case 'hero-confirm':
        return true; // Just a confirmation
      case 'character-select':
        return state.selectedCharacter !== '';
      case 'pick-hook':
        return state.selectedHook !== '' || state.filmHooks.length === 0;
      case 'reading-mode':
        return true; // Has default value
      case 'story-length':
        return true; // Has default value
      case 'custom-confirm':
        return true; // Auto-continue
      case 'production-started':
        return true; // Continue button
      case 'custom-paywall':
        return true; // Accept/Maybe Later buttons
      case 'who-for':
        return state.isForSelf !== null;
      case 'profile':
        if (state.isForSelf) {
          return state.profileName.trim() !== '' && state.decadeKid !== '';
        }
        return state.profileName.trim() !== '' && state.ageRange !== '';
      case 'avatar':
        // If "for me and my kid", need both avatars selected
        if (!state.isForSelf) {
          return state.avatarUrl !== '' && state.childAvatarUrl !== '';
        }
        // Otherwise just need parent avatar
        return state.avatarUrl !== '';
      case 'profile-confirm':
        return true; // Auto-continue after animation
      case 'interests':
        return state.interests.length >= 1;
      case 'goal':
        return state.readingGoal > 0;
      case 'building-library':
        return true; // Auto-advances
      case 'app-preview':
        return true;
      case 'reader-preview':
        return true;
      default:
        return false;
    }
  };

  // Get the parent's name - we could get this from signup or use a default
  const parentName = localStorage.getItem('signup_first_name') || 'You';

  const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
  };

  const renderBookGrid = (books: Book[], isMoreStep: boolean) => {
    const selectedList = isMoreStep ? state.moreBooks : state.selectedBooks;
    
    return (
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 px-1">
        {books.map((book) => (
          <motion.button
            key={book.id}
            onClick={() => toggleBook(book.id, isMoreStep)}
            whileTap={{ scale: 0.95 }}
            className="relative aspect-[2/3] rounded-xl overflow-hidden group"
          >
            <img
              src={book.cover_image_url || '/placeholder.svg'}
              alt={book.title}
              className="w-full h-full object-cover"
            />
            {/* Selection overlay */}
            <div className={`absolute inset-0 transition-all duration-200 ${
              selectedList.includes(book.id)
                ? 'bg-primary/40 border-2 border-primary'
                : 'bg-black/0 group-hover:bg-black/20'
            } rounded-xl`}>
              {/* Selection indicator - checkmark when selected, empty circle when not */}
              <div className={`absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 ${
                selectedList.includes(book.id)
                  ? 'bg-primary shadow-lg'
                  : 'bg-white/20 border-2 border-white shadow-md backdrop-blur-sm'
              }`}>
                {selectedList.includes(book.id) && (
                  <Check className="w-4 h-4 text-primary-foreground" />
                )}
              </div>
            </div>
          </motion.button>
        ))}
      </div>
    );
  };

  const renderContent = () => (
    <>
      {/* Header */}
      <div className="absolute top-3 left-3 right-3 z-20 flex justify-between pt-safe">
        {step !== 'library' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="w-10 h-10 p-0 rounded-full bg-white/10 backdrop-blur-xl border-0 hover:bg-white/20 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-foreground" />
          </Button>
        ) : (
          <div className="w-10" />
        )}
        <div className="flex items-center gap-1.5">
          {steps.map((s, i) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                step === s ? 'bg-primary w-6' : i < currentStepIndex ? 'bg-primary/60 w-2' : 'bg-white/20 w-2'
              }`}
            />
          ))}
        </div>
        <div className="w-10" />
      </div>

      <div className="flex-1 flex flex-col px-4 sm:px-6 pt-20 pb-6 w-full overflow-auto">
        <AnimatePresence mode="wait">
          {step === 'library' && (
            <motion.div
              key="library"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col"
            >
              <div className="text-center space-y-2 mb-6">
                <p className="text-primary/80 text-sm font-medium tracking-wide uppercase">
                  Welcome to BuxBox
                </p>
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight leading-tight">
                  We have a growing library
                </h2>
                <p className="text-foreground/70 text-lg">
                  of Hollywood Kids book adaptations
                </p>
                <p className="text-foreground/50 text-sm">
                  Tap to select a few favorites
                </p>
              </div>
              
              <div className="flex-1 overflow-auto -mx-4 px-4">
                {isLoading ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} className="aspect-[2/3] rounded-xl bg-white/10 animate-pulse" />
                    ))}
                  </div>
                ) : (
                  renderBookGrid(displayBooks, false)
                )}
              </div>

              <div className="pt-6 flex justify-center">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'more' && (
            <motion.div
              key="more"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col"
            >
              <div className="text-center space-y-3 mb-6">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Good picks! 👏
                </h2>
                <p className="text-foreground/70 text-lg">
                  We'll show more like this
                </p>
                <p className="text-foreground/50 text-sm">
                  Select a few more if you'd like
                </p>
              </div>
              
              <div className="flex-1 overflow-auto -mx-4 px-4">
                {renderBookGrid(remainingBooks, true)}
              </div>

              <div className="pt-6 flex justify-center">
                <Button
                  onClick={handleContinue}
                  className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'reading-time' && (
            <motion.div
              key="reading-time"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  When does reading usually happen?
                </h2>
              </div>
              
              <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                {READING_TIMES.map(({ id, label, icon: Icon }) => (
                  <motion.button
                    key={id}
                    onClick={() => setState(prev => ({ ...prev, readingTime: id }))}
                    whileTap={{ scale: 0.97 }}
                    className={`flex flex-col items-center justify-center gap-3 p-6 rounded-2xl transition-all ${
                      state.readingTime === id
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <Icon className={`w-8 h-8 ${state.readingTime === id ? 'text-primary' : 'text-foreground/70'}`} />
                    <span className={`text-sm font-medium text-center ${state.readingTime === id ? 'text-foreground' : 'text-foreground/70'}`}>
                      {label}
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'favourite-film' && (
            <motion.div
              key="favourite-film"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  What's your all-time favourite film?
                </h2>
                <p className="text-foreground/50 text-sm">
                  We'll use this to find stories you'll love
                </p>
              </div>
              
              <div className="w-full max-w-md px-4">
                <input
                  type="text"
                  value={state.favouriteFilm}
                  onChange={(e) => setState(prev => ({ ...prev, favouriteFilm: e.target.value }))}
                  placeholder="e.g. Independence Day, American Pie..."
                  className="w-full h-14 px-5 rounded-2xl bg-white/10 border border-white/20 text-foreground placeholder:text-foreground/40 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-lg"
                />
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!state.favouriteFilm.trim()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Personalized Story Step - AI generated story */}
          {step === 'personalized-story' && (
            <motion.div
              key="personalized-story"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4 relative overflow-hidden"
            >
              {/* Ambient background effect */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
                <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-primary/10 rounded-full blur-3xl" />
              </div>

              <AnimatePresence mode="wait">
                {/* Generating Story */}
                {storyStage === 'generating' && (
                  <motion.div
                    key="generating"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex flex-col items-center justify-center z-10"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                      className="w-16 h-16 rounded-full border-4 border-primary/20 border-t-primary mb-6"
                    />
                    <h2 className="text-xl sm:text-2xl font-semibold text-foreground text-center mb-2">
                      Creating your story...
                    </h2>
                    <p className="text-foreground/60 text-center">
                      Starring <span className="text-primary font-medium">{state.profileName || 'the hero'}</span>
                    </p>
                  </motion.div>
                )}

                {/* Display Story */}
                {storyStage === 'story' && (
                  <motion.div
                    key="story-display"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="flex flex-col items-center justify-center z-10 w-full max-w-lg px-2"
                  >
                    {/* Story card */}
                    <motion.div
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.2 }}
                      className="w-full rounded-3xl bg-white/5 backdrop-blur-sm border border-white/10 p-6 sm:p-8 mb-6"
                    >
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="w-4 h-4 text-primary" />
                        <span className="text-xs uppercase tracking-wider text-foreground/50">
                          Starring {state.profileName || 'the hero'}
                        </span>
                      </div>
                      
                      <div className="text-foreground/90 text-lg sm:text-xl leading-relaxed font-apple font-semibold space-y-4">
                        {displayedStoryText.split('\n\n').map((paragraph, idx) => (
                          <p key={idx}>{paragraph}</p>
                        ))}
                        {displayedStoryText.length < state.generatedStory.length && (
                          <motion.span
                            animate={{ opacity: [1, 0] }}
                            transition={{ duration: 0.5, repeat: Infinity }}
                            className="inline-block w-0.5 h-5 bg-primary ml-1"
                          />
                        )}
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.8 }}
                      className="flex flex-col gap-3 w-full"
                    >
                      <Button
                        onClick={handleContinue}
                        className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                        style={{ borderRadius: '14px / 18px' }}
                      >
                        Continue
                      </Button>
                      
                      <Button
                        onClick={() => {
                          setDisplayedStoryText('');
                          setState(prev => ({ ...prev, generatedStory: '' }));
                          setStoryStage('generating');
                          generatePersonalizedStory();
                        }}
                        variant="ghost"
                        className="w-full h-11 text-foreground/50 hover:text-foreground/70 hover:bg-white/5 text-sm"
                        style={{ borderRadius: '12px' }}
                      >
                        Regenerate story
                      </Button>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Custom Offer - Limited Time Offer (shown at end of onboarding) */}
          {step === 'custom-offer' && !state.filmTaken && (
            <motion.div
              key="custom-offer"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              {/* Intro text referencing earlier film choice */}
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 0.1 }}
                className="text-foreground/70 text-center text-sm mb-4"
              >
                Earlier you told us your favourite film was...
              </motion.p>
              
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-3xl sm:text-4xl font-bold text-primary tracking-tight text-center mb-4"
              >
                {state.favouriteFilm}
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-foreground/80 text-center text-lg font-medium mb-2"
              >
                Great choice! ✨
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ delay: 0.5 }}
                className="text-foreground/70 text-center text-base max-w-sm mb-8"
              >
                We don't have that one in our collection yet, but we have a pretty cool idea...
              </motion.p>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="w-full max-w-sm"
              >
                <Button
                  onClick={() => {
                    setState(prev => ({ ...prev, wantsCustomBook: true }));
                    setStep('hero-confirm');
                  }}
                  className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Film Alternatives - When film is already taken */}
          {(step === 'custom-offer' && state.filmTaken) && (
            <motion.div
              key="film-alternatives"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 0.1 }}
                className="text-foreground/70 text-center text-sm mb-4"
              >
                Earlier you suggested...
              </motion.p>
              
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl sm:text-3xl font-bold text-primary tracking-tight text-center mb-4"
              >
                {state.originalFilmName || state.favouriteFilm}
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-foreground/80 text-center text-base font-medium mb-2"
              >
                Great minds think alike! 🎬
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                transition={{ delay: 0.5 }}
                className="text-foreground/70 text-center text-base max-w-sm mb-6"
              >
                That film was already claimed, but we think you'll love one of these...
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col gap-3 w-full max-w-md mb-6"
              >
                {state.alternativeFilms.map((alt, index) => (
                  <motion.button
                    key={alt.filmName}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    onClick={() => {
                      // Swap in the alternative's pre-fetched data
                      setState(prev => ({
                        ...prev,
                        favouriteFilm: alt.filmName,
                        filmHooks: alt.hooks,
                        filmCharacters: alt.characters,
                        filmTaken: false, // Now they have a valid choice
                        wantsCustomBook: true,
                      }));
                      setStep('hero-confirm');
                    }}
                    className="p-4 rounded-2xl text-left transition-all bg-white/10 border border-white/20 hover:bg-primary/20 hover:border-primary/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Film className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground">{alt.filmName}</h3>
                        <p className="text-sm text-foreground/60">{alt.tagline}</p>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* Hero Confirm - Would you like [name] to be in the story? */}
          {step === 'hero-confirm' && (
            <motion.div
              key="hero-confirm"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="w-24 h-24 rounded-full overflow-hidden border-4 border-primary/50 mb-6"
              >
                <img 
                  src={state.childAvatarUrl || state.avatarUrl || '/placeholder.svg'} 
                  alt="Profile" 
                  className="w-full h-full object-cover"
                />
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight text-center mb-3"
              >
                Create a story starring <span className="text-primary">{state.profileName || 'your child'}</span>
              </motion.h2>
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 0.3 }}
                className="text-foreground/70 text-center text-base max-w-sm mb-4 space-y-4"
              >
                <p>
                  Submit {state.profileName || 'their'}'s favourite film and character details.
                  We'll send this to our authors and illustrators to review and create a custom story.
                </p>
                <p className="text-foreground/50 text-sm">
                  This is part of your BuxBox subscription — no templates, no AI shortcuts.
                  Every accepted story is written and illustrated by our team.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="w-full max-w-sm"
              >
                <Button
                  onClick={handleContinue}
                  className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Yes! ✨
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Character Select - Who should [name] become? */}
          {step === 'character-select' && (
            <motion.div
              key="character-select"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Would you like <span className="text-primary">{state.profileName || 'your child'}</span> to be...
                </h2>
                <p className="text-foreground/50 text-sm">
                  Choose a character from {state.favouriteFilm}
                </p>
              </div>
              
              <div className="flex flex-col gap-3 w-full max-w-md">
                {state.filmCharacters.length > 0 ? (
                  state.filmCharacters.map((character, index) => (
                    <motion.button
                      key={character.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      onClick={() => setState(prev => ({ ...prev, selectedCharacter: character.id }))}
                      className={`p-4 rounded-2xl text-left transition-all ${
                        state.selectedCharacter === character.id
                          ? 'bg-primary/30 border-2 border-primary'
                          : 'bg-white/10 border border-white/20 hover:bg-white/15'
                      }`}
                    >
                      <h3 className={`font-semibold mb-1 ${state.selectedCharacter === character.id ? 'text-foreground' : 'text-foreground/80'}`}>
                        {character.name}
                      </h3>
                      <p className="text-sm text-foreground/60">{character.description}</p>
                    </motion.button>
                  ))
                ) : (
                  /* Fallback when no characters are available - show "The Hero" option */
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    onClick={() => setState(prev => ({ ...prev, selectedCharacter: 'hero' }))}
                    className={`p-4 rounded-2xl text-left transition-all ${
                      state.selectedCharacter === 'hero'
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <h3 className={`font-semibold mb-1 ${state.selectedCharacter === 'hero' ? 'text-foreground' : 'text-foreground/80'}`}>
                      The Hero ✨
                    </h3>
                    <p className="text-sm text-foreground/60">
                      {state.profileName || 'Your child'} will be the main character of the story
                    </p>
                  </motion.button>
                )}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Production Started - "Your Film is in Motion!" */}
          {step === 'production-started' && (
            <motion.div
              key="production-started"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-6 py-8"
            >
              {/* Animated BuxBox logo */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', damping: 12, delay: 0.1 }}
                className="mb-6"
              >
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center backdrop-blur-md">
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                  >
                    <img 
                      src="/logo.png" 
                      alt="BuxBox" 
                      className="w-12 h-12 object-contain"
                    />
                  </motion.div>
                </div>
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight text-center mb-2"
              >
                Your film is in motion!
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.8 }}
                transition={{ delay: 0.5 }}
                className="text-foreground/70 text-center text-base mb-2"
              >
                The magic has begun.
              </motion.p>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                transition={{ delay: 0.7 }}
                className="text-foreground/60 text-center text-sm max-w-sm mb-10 leading-relaxed"
              >
                Your favourite film has been sent to our brilliant writers and talented illustrators. The next step is underway — watch for your unique book cover to appear in the 'Coming Soon' section within the next 24–48 hours.
              </motion.p>
              
              {/* Production Timeline */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                className="w-full max-w-xs mb-10"
              >
                <div className="flex flex-col items-center space-y-0">
                  {/* Step 1: Preparation */}
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.1 }}
                    className="flex items-center gap-3 w-full"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/20 border-2 border-primary flex items-center justify-center">
                      <motion.div
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                        className="w-2 h-2 rounded-full bg-primary"
                      />
                    </div>
                    <span className="text-foreground/80 text-sm font-medium">A little preparation</span>
                  </motion.div>
                  
                  {/* Connecting line */}
                  <motion.div 
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ delay: 1.3, duration: 0.3 }}
                    className="w-0.5 h-6 bg-foreground/20 ml-4 origin-top"
                  />
                  
                  {/* Step 2: Writing */}
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.5 }}
                    className="flex items-center gap-3 w-full"
                  >
                    <div className="w-8 h-8 rounded-full bg-foreground/10 border-2 border-foreground/30 flex items-center justify-center">
                      <BookOpen className="w-3.5 h-3.5 text-foreground/50" />
                    </div>
                    <span className="text-foreground/50 text-sm">Writing story</span>
                  </motion.div>
                  
                  {/* Connecting line */}
                  <motion.div 
                    initial={{ scaleY: 0 }}
                    animate={{ scaleY: 1 }}
                    transition={{ delay: 1.7, duration: 0.3 }}
                    className="w-0.5 h-6 bg-foreground/20 ml-4 origin-top"
                  />
                  
                  {/* Step 3: Art */}
                  <motion.div 
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 1.9 }}
                    className="flex items-center gap-3 w-full"
                  >
                    <div className="w-8 h-8 rounded-full bg-foreground/10 border-2 border-foreground/30 flex items-center justify-center">
                      <Wand2 className="w-3.5 h-3.5 text-foreground/50" />
                    </div>
                    <span className="text-foreground/50 text-sm">Finalising art</span>
                  </motion.div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2.2 }}
                className="w-full max-w-sm"
              >
                <Button
                  onClick={handleContinue}
                  className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Custom Paywall - Glass morphism payment sheet style */}
          {step === 'custom-paywall' && (
            <motion.div
              key="custom-paywall"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4 py-6"
            >
              <motion.div
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="w-full max-w-md mx-auto"
              >
                <div className="glass-morphism relative rounded-2xl">
                  {/* Glass logo at top */}
                  <div className="absolute -top-6 left-1/2 -translate-x-1/2 z-10">
                    <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 flex items-center justify-center shadow-lg">
                      <img 
                        src="/logo.png" 
                        alt="Logo" 
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                  </div>
                  
                  <div className="relative px-5 py-10 pt-12 flex flex-col items-center justify-center w-full overflow-hidden rounded-2xl">
                    <div className="w-full max-w-sm mx-auto space-y-5">
                      {/* Header */}
                      <div className="text-center space-y-1">
                        <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                          Subscribe Now
                        </h1>
                        <p className="text-sm sm:text-base text-foreground/80">
                          Unlimited access to all books
                        </p>
                      </div>

                      {/* Pricing Display */}
                      <div className="rounded-xl p-5 bg-white/5 border border-white/10">
                        <div className="text-center space-y-2">
                          <div className="text-base sm:text-lg text-foreground/90 font-medium">
                            from £3.99 per month
                          </div>
                        </div>
                      </div>

                      {/* Benefits */}
                      <div className="space-y-2">
                        <div className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-foreground/80 text-sm">Your custom "{state.favouriteFilm}" book</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-foreground/80 text-sm">Entire library + new releases</span>
                        </div>
                        
                        <div className="flex items-center gap-3 p-2.5 rounded-lg bg-white/5 border border-white/10">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <span className="text-foreground/80 text-sm">Up to 5 profiles, cancel anytime</span>
                        </div>
                      </div>

                      {/* Buttons */}
                      <div className="space-y-2.5 pt-2">
                        {/* Primary CTA Button with shimmer */}
                        <Button
                          onClick={async () => {
                            setIsProcessingPayment(true);
                            await saveCustomBookRequest();
                            await saveOnboardingData();
                            setIsProcessingPayment(false);
                            setShowPaymentModal(true);
                          }}
                          disabled={isProcessingPayment}
                          className="relative w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-base overflow-hidden"
                          style={{ borderRadius: '14px' }}
                        >
                          <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                            animate={{ x: ['-100%', '100%'] }}
                            transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                          />
                          <span className="relative z-10">
                            {isProcessingPayment ? 'Loading...' : 'Subscribe Now'}
                          </span>
                        </Button>
                        
                        {/* Maybe Later Button */}
                        <Button
                          onClick={() => setShowCancelConfirmation(true)}
                          variant="ghost"
                          className="w-full h-11 text-foreground/50 hover:text-foreground/70 hover:bg-white/5 text-sm"
                          style={{ borderRadius: '12px' }}
                        >
                          Maybe later
                        </Button>
                      </div>

                      {/* Trust text */}
                      <p className="text-center text-xs text-foreground/50 px-2">
                        Cancel anytime. Secure payment.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Cancel Confirmation Modal */}
              <AnimatePresence>
                {showCancelConfirmation && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-6"
                    onClick={() => setShowCancelConfirmation(false)}
                  >
                    <motion.div
                      initial={{ scale: 0.9, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.9, opacity: 0 }}
                      onClick={(e) => e.stopPropagation()}
                      className="w-full max-w-sm glass-morphism rounded-2xl p-6 text-center"
                    >
                      <h3 className="text-xl font-semibold text-foreground mb-3">
                        Are you sure?
                      </h3>
                      <p className="text-foreground/70 text-sm mb-6 leading-relaxed">
                        If you proceed now, the production of your "{state.favouriteFilm}" book will be cancelled. Are you certain you want to stop the magic?
                      </p>
                      
                      <div className="space-y-3">
                        <Button
                          onClick={() => setShowCancelConfirmation(false)}
                          className="w-full h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-base"
                          style={{ borderRadius: '14px' }}
                        >
                          No, Continue Production
                        </Button>
                        
                        <Button
                          onClick={async () => {
                            setShowCancelConfirmation(false);
                            await saveOnboardingData();
                            navigate('/');
                          }}
                          variant="ghost"
                          className="w-full h-10 text-foreground/50 hover:text-foreground/70 text-sm"
                        >
                          Yes, Cancel
                        </Button>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {/* Pick Hook - Choose Favourite Moment */}
          {step === 'pick-hook' && (
            <motion.div
              key="pick-hook"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Choose your favourite moment
                </h2>
                <p className="text-foreground/50 text-sm">
                  This will be the heart of the story
                </p>
              </div>
              
              <div className="flex flex-col gap-3 w-full max-w-md">
                {state.filmHooks.length > 0 ? (
                  state.filmHooks.map((hook, index) => (
                    <motion.button
                      key={hook.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      onClick={() => setState(prev => ({ ...prev, selectedHook: hook.id }))}
                      className={`p-4 rounded-2xl text-left transition-all ${
                        state.selectedHook === hook.id
                          ? 'bg-primary/30 border-2 border-primary'
                          : 'bg-white/10 border border-white/20 hover:bg-white/15'
                      }`}
                    >
                      <h3 className={`font-semibold mb-1 ${state.selectedHook === hook.id ? 'text-foreground' : 'text-foreground/80'}`}>
                        {hook.title}
                      </h3>
                      <p className="text-sm text-foreground/60">{hook.description}</p>
                    </motion.button>
                  ))
                ) : (
                  <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 }}
                    onClick={() => setState(prev => ({ ...prev, selectedHook: 'main_scene' }))}
                    className={`p-4 rounded-2xl text-left transition-all ${
                      state.selectedHook === 'main_scene'
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <h3 className={`font-semibold mb-1 ${state.selectedHook === 'main_scene' ? 'text-foreground' : 'text-foreground/80'}`}>
                      The main scene 🎬
                    </h3>
                    <p className="text-sm text-foreground/60">
                      We'll pick the best iconic scene from {state.favouriteFilm}
                    </p>
                  </motion.button>
                )}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Reading Mode - Listen / Read / Both */}
          {step === 'reading-mode' && (
            <motion.div
              key="reading-mode"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  How would you like to read?
                </h2>
              </div>
              
              <div className="grid grid-cols-3 gap-3 w-full max-w-md">
                {[
                  { id: 'listen' as const, label: 'Listen Along', icon: Headphones, desc: 'Audio narration' },
                  { id: 'read' as const, label: 'Read Along', icon: BookOpen, desc: 'Text only' },
                  { id: 'both' as const, label: 'Both', icon: Sparkles, desc: 'Best experience' },
                ].map(({ id, label, icon: Icon, desc }) => (
                  <motion.button
                    key={id}
                    onClick={() => setState(prev => ({ ...prev, readingMode: id }))}
                    whileTap={{ scale: 0.97 }}
                    className={`flex flex-col items-center justify-center gap-2 p-5 rounded-2xl transition-all ${
                      state.readingMode === id
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <Icon className={`w-8 h-8 ${state.readingMode === id ? 'text-primary' : 'text-foreground/70'}`} />
                    <span className={`text-sm font-medium text-center ${state.readingMode === id ? 'text-foreground' : 'text-foreground/70'}`}>
                      {label}
                    </span>
                    <span className="text-xs text-foreground/50">{desc}</span>
                  </motion.button>
                ))}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Story Length - Short / Standard / Epic */}
          {step === 'story-length' && (
            <motion.div
              key="story-length"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  How long should it be?
                </h2>
              </div>
              
              <div className="flex flex-col gap-3 w-full max-w-md">
                {[
                  { id: 'short' as const, label: 'Short and Sweet', time: '~5 mins', icon: '🌙', desc: 'Perfect for quick bedtimes' },
                  { id: 'standard' as const, label: 'Bedtime Standard', time: '~10 mins', icon: '📚', desc: 'The classic story length' },
                  { id: 'epic' as const, label: 'Epic Journey', time: '~20+ mins', icon: '🚀', desc: 'For the true adventurers' },
                ].map(({ id, label, time, icon, desc }) => (
                  <motion.button
                    key={id}
                    onClick={() => setState(prev => ({ ...prev, storyLength: id }))}
                    whileTap={{ scale: 0.98 }}
                    className={`flex items-center gap-4 p-5 rounded-2xl text-left transition-all ${
                      state.storyLength === id
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <span className="text-3xl">{icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${state.storyLength === id ? 'text-foreground' : 'text-foreground/80'}`}>
                          {label}
                        </span>
                        <span className="text-xs text-foreground/50 bg-white/10 px-2 py-0.5 rounded-full">
                          {time}
                        </span>
                      </div>
                      <p className="text-sm text-foreground/60">{desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      state.storyLength === id ? 'border-primary bg-primary' : 'border-white/40'
                    }`}>
                      {state.storyLength === id && <Check className="w-4 h-4 text-primary-foreground" />}
                    </div>
                  </motion.button>
                ))}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Custom Confirm - Great choice! */}
          {step === 'custom-confirm' && (
            <motion.div
              key="custom-confirm"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center px-4"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', damping: 15 }}
                className="mb-6 w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center"
              >
                <Check className="w-10 h-10 text-white" />
              </motion.div>
              
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight text-center mb-3"
              >
                Great choice! ✨
              </motion.h2>
              
              <motion.p 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-foreground/70 text-center text-base max-w-sm mb-4"
              >
                After this, we'll send your request to our top Authors and team of Illustrators to start creating the book.
              </motion.p>
              
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.6 }}
                transition={{ delay: 0.7 }}
                className="bg-white/5 rounded-xl p-4 max-w-sm w-full mb-8"
              >
                <div className="text-sm space-y-2 text-foreground/70">
                  <div className="flex justify-between">
                    <span>Film:</span>
                    <span className="text-foreground font-medium">{state.favouriteFilm}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Hero:</span>
                    <span className="text-foreground font-medium">{state.profileName || 'Your child'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Style:</span>
                    <span className="text-foreground font-medium capitalize">{state.readingMode === 'both' ? 'Listen & Read' : state.readingMode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Length:</span>
                    <span className="text-foreground font-medium capitalize">{state.storyLength}</span>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="flex justify-center w-full"
              >
                <Button
                  onClick={handleContinue}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {step === 'who-for' && (
            <motion.div
              key="who-for"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Who will be reading?
                </h2>
                <p className="text-foreground/60 text-base max-w-sm mx-auto">
                  We'll personalize your experience
                </p>
              </div>
              
              <div className="flex flex-col gap-4 w-full max-w-md">
                <motion.button
                  onClick={() => setState(prev => ({ ...prev, isForSelf: true }))}
                  whileTap={{ scale: 0.97 }}
                  className={`flex items-center gap-5 p-5 rounded-2xl transition-all text-left ${
                    state.isForSelf === true
                      ? 'bg-primary/30 border-2 border-primary'
                      : 'bg-white/10 border-2 border-white/20 hover:bg-white/15'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    state.isForSelf === true ? 'bg-primary/30' : 'bg-white/10'
                  }`}>
                    <User className={`w-7 h-7 ${state.isForSelf === true ? 'text-primary' : 'text-foreground/70'}`} />
                  </div>
                  <div className="flex-1">
                    <span className={`text-lg font-semibold block ${state.isForSelf === true ? 'text-foreground' : 'text-foreground/80'}`}>
                      For me
                    </span>
                    <span className="text-sm text-foreground/50">
                      I'll be enjoying the stories myself
                    </span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    state.isForSelf === true ? 'border-primary bg-primary' : 'border-white/40'
                  }`}>
                    {state.isForSelf === true && <Check className="w-4 h-4 text-primary-foreground" />}
                  </div>
                </motion.button>
                
                <motion.button
                  onClick={() => setState(prev => ({ ...prev, isForSelf: false }))}
                  whileTap={{ scale: 0.97 }}
                  className={`flex items-center gap-5 p-5 rounded-2xl transition-all text-left ${
                    state.isForSelf === false
                      ? 'bg-primary/30 border-2 border-primary'
                      : 'bg-white/10 border-2 border-white/20 hover:bg-white/15'
                  }`}
                >
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    state.isForSelf === false ? 'bg-primary/30' : 'bg-white/10'
                  }`}>
                    <Users className={`w-7 h-7 ${state.isForSelf === false ? 'text-primary' : 'text-foreground/70'}`} />
                  </div>
                  <div className="flex-1">
                    <span className={`text-lg font-semibold block ${state.isForSelf === false ? 'text-foreground' : 'text-foreground/80'}`}>
                      For me and my kid
                    </span>
                    <span className="text-sm text-foreground/50">
                      We'll read together or they'll listen solo
                    </span>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    state.isForSelf === false ? 'border-primary bg-primary' : 'border-white/40'
                  }`}>
                    {state.isForSelf === false && <Check className="w-4 h-4 text-primary-foreground" />}
                  </div>
                </motion.button>
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'profile' && (
            <motion.div
              key="profile"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  {state.isForSelf ? state.profileName || 'Let\'s set the scene' : 'Let\'s set the scene'}
                </h2>
              </div>
              
              <div className="w-full max-w-md space-y-6">
                <div className="space-y-2">
                  <label className="text-sm text-foreground/70 px-1">
                    {state.isForSelf ? "What's your name?" : "What's their name?"}
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter name"
                    value={state.profileName}
                    onChange={(e) => setState(prev => ({ ...prev, profileName: e.target.value }))}
                    className="w-full h-14 px-4 bg-white/10 border-0 backdrop-blur-xl text-foreground placeholder:text-foreground/50 text-lg focus-visible:ring-0 focus-visible:ring-offset-0"
                    style={{ borderRadius: '14px / 18px' }}
                    autoFocus
                  />
                </div>

                {state.isForSelf ? (
                  <div className="space-y-3">
                    <label className="text-sm text-foreground/70 px-1">What decade kid are you?</label>
                    <div className="grid grid-cols-4 gap-3">
                      {DECADES.map((decade) => (
                        <motion.button
                          key={decade}
                          onClick={() => setState(prev => ({ ...prev, decadeKid: decade }))}
                          whileTap={{ scale: 0.95 }}
                          className={`py-4 rounded-xl font-medium transition-all ${
                            state.decadeKid === decade
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-white/10 text-foreground/70 hover:bg-white/15'
                          }`}
                        >
                          {decade}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className="text-sm text-foreground/70 px-1">Age Range</label>
                    <div className="flex gap-3">
                      {AGE_RANGES.map((range) => (
                        <motion.button
                          key={range}
                          onClick={() => setState(prev => ({ ...prev, ageRange: range }))}
                          whileTap={{ scale: 0.95 }}
                          className={`flex-1 py-4 rounded-xl font-medium transition-all ${
                            state.ageRange === range
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-white/10 text-foreground/70 hover:bg-white/15'
                          }`}
                        >
                          {range}
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'avatar' && (
            <motion.div
              key="avatar"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              <div className="text-center space-y-2 mb-4">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  Choose your profile pictures
                </h2>
                <p className="text-foreground/50 text-sm">
                  Pick a favorite character for each reader
                </p>
              </div>
              
              <div className="flex-1 overflow-auto space-y-6">
                {/* Parent's profile selection */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 px-1">
                    {state.avatarUrl ? (
                      <img src={state.avatarUrl} alt="Your avatar" className="w-10 h-10 rounded-full object-cover border-2 border-primary" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center">
                        <User className="w-5 h-5 text-foreground/50" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-foreground">{parentName}</p>
                      <p className="text-xs text-foreground/50">Your profile</p>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide">
                    <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                      {randomizedAvatars.map((avatar) => {
                        const isSelected = state.avatarUrl === avatar.image_url;
                        return (
                          <motion.button
                            key={`parent-${avatar.id}`}
                            onClick={() => setState(prev => ({ ...prev, avatarUrl: avatar.image_url }))}
                            whileTap={{ scale: 0.95 }}
                            className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex-shrink-0"
                          >
                            <img
                              src={avatar.image_url}
                              alt="Avatar"
                              className="w-full h-full object-cover"
                            />
                            <div className={`absolute inset-0 transition-all duration-200 rounded-full ${
                              isSelected
                                ? 'bg-primary/40 border-4 border-primary'
                                : 'bg-black/0 hover:bg-black/20 border-2 border-transparent'
                            }`}>
                              {isSelected && (
                                <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                  <Check className="w-4 h-4 text-primary-foreground" />
                                </div>
                              )}
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Kid's profile selection - only show if isForSelf is false */}
                {!state.isForSelf && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 px-1">
                      {state.childAvatarUrl ? (
                        <img src={state.childAvatarUrl} alt="Kid avatar" className="w-10 h-10 rounded-full object-cover border-2 border-primary" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center">
                          <Baby className="w-5 h-5 text-foreground/50" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-medium text-foreground">{state.profileName || 'Your kid'}</p>
                        <p className="text-xs text-foreground/50">Their profile</p>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto -mx-4 px-4 scrollbar-hide">
                      <div className="flex gap-3 pb-2" style={{ width: 'max-content' }}>
                        {randomizedAvatars.map((avatar) => {
                          const isSelected = state.childAvatarUrl === avatar.image_url;
                          return (
                            <motion.button
                              key={`child-${avatar.id}`}
                              onClick={() => setState(prev => ({ 
                                ...prev, 
                                childAvatarUrl: avatar.image_url,
                                addChildProfile: true 
                              }))}
                              whileTap={{ scale: 0.95 }}
                              className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden flex-shrink-0"
                            >
                              <img
                                src={avatar.image_url}
                                alt="Avatar"
                                className="w-full h-full object-cover"
                              />
                              <div className={`absolute inset-0 transition-all duration-200 rounded-full ${
                                isSelected
                                  ? 'bg-primary/40 border-4 border-primary'
                                  : 'bg-black/0 hover:bg-black/20 border-2 border-transparent'
                              }`}>
                                {isSelected && (
                                  <div className="absolute bottom-0 right-0 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                    <Check className="w-4 h-4 text-primary-foreground" />
                                  </div>
                                )}
                              </div>
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="pt-6 flex justify-center">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'profile-confirm' && (
            <motion.div
              key="profile-confirm"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-10">
                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight"
                >
                  {(state.addChildProfile || !state.isForSelf) ? 'Your profiles are ready!' : 'Your profile is ready!'}
                </motion.h2>
              </div>
              
              <div className="flex items-center justify-center gap-8 sm:gap-12">
                {/* Parent Profile */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ delay: 0.2, type: 'spring', damping: 20 }}
                  className="flex flex-col items-center gap-3"
                >
                  <div 
                    className="relative p-1.5 rounded-full"
                    style={{
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)'
                    }}
                  >
                    <div className="relative overflow-hidden rounded-full">
                      <img 
                        src={state.avatarUrl} 
                        alt="Your avatar" 
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover"
                      />
                      <div 
                        className="absolute top-0 left-0 w-full h-full rounded-full"
                        style={{
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)'
                        }} 
                      />
                    </div>
                  </div>
                  <span className="text-lg font-medium text-foreground capitalize">
                    {parentName}
                  </span>
                </motion.div>

                {/* Child Profile - show if isForSelf is false OR addChildProfile is true */}
                {(!state.isForSelf || state.addChildProfile) && state.childAvatarUrl && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ delay: 0.4, type: 'spring', damping: 20 }}
                    className="flex flex-col items-center gap-3"
                  >
                    <div 
                      className="relative p-1.5 rounded-full"
                      style={{
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37), inset 0 1px 0 0 rgba(255, 255, 255, 0.3)'
                      }}
                    >
                      <div className="relative overflow-hidden rounded-full">
                        <img 
                          src={state.childAvatarUrl} 
                          alt="Child avatar" 
                          className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover"
                        />
                        <div 
                          className="absolute top-0 left-0 w-full h-full rounded-full"
                          style={{
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 0 20px rgba(255, 255, 255, 0.2)'
                          }} 
                        />
                      </div>
                    </div>
                    <span className="text-lg font-medium text-foreground capitalize">
                      {state.profileName || 'Child'}
                    </span>
                  </motion.div>
                )}
              </div>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
                className="pt-10 flex justify-center w-full"
              >
                <Button
                  onClick={handleContinue}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {step === 'interests' && (
            <motion.div
              key="interests"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  What makes {state.profileName || 'them'} light up?
                </h2>
                <p className="text-foreground/50 text-sm">
                  Select all that apply
                </p>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full max-w-lg">
                {INTERESTS.map(({ id, label, icon: Icon }) => (
                  <motion.button
                    key={id}
                    onClick={() => toggleInterest(id)}
                    whileTap={{ scale: 0.97 }}
                    className={`flex flex-col items-center justify-center gap-3 p-5 rounded-2xl transition-all ${
                      state.interests.includes(id)
                        ? 'bg-primary/30 border-2 border-primary'
                        : 'bg-white/10 border border-white/20 hover:bg-white/15'
                    }`}
                  >
                    <Icon className={`w-8 h-8 ${state.interests.includes(id) ? 'text-primary' : 'text-foreground/70'}`} />
                    <span className={`text-sm font-medium text-center ${state.interests.includes(id) ? 'text-foreground' : 'text-foreground/70'}`}>
                      {label}
                    </span>
                  </motion.button>
                ))}
              </div>

              <div className="pt-8 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {step === 'goal' && (
            <motion.div
              key="goal"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center"
            >
              <div className="text-center space-y-3 mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                  How many books do you aim to read a month?
                </h2>
              </div>
              
              <div className="flex gap-3 w-full max-w-md justify-center">
                {READING_GOALS.map((num) => (
                  <motion.button
                    key={num}
                    onClick={() => setState(prev => ({ ...prev, readingGoal: num }))}
                    whileTap={{ scale: 0.95 }}
                    className={`w-16 h-16 rounded-2xl font-bold text-2xl transition-all ${
                      state.readingGoal === num
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-white/10 text-foreground/70 hover:bg-white/15'
                    }`}
                  >
                    {num}
                  </motion.button>
                ))}
              </div>

              <div className="pt-10 flex justify-center w-full">
                <Button
                  onClick={handleContinue}
                  disabled={!canContinue()}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px] disabled:opacity-50"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </div>
            </motion.div>
          )}

          {/* Building Library Animation Step - show kid's profile if "for me and my kid" */}
          {step === 'building-library' && (
            <BuildingLibraryStep 
              displayBooks={displayBooks}
              avatarUrl={!state.isForSelf ? state.childAvatarUrl : state.avatarUrl}
              profileName={!state.isForSelf ? (state.profileName || 'Your kid') : parentName}
              onComplete={() => setStep('app-preview')}
            />
          )}

          {step === 'app-preview' && (
            <motion.div
              key="app-preview"
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
              className="flex-1 flex flex-col items-center justify-center py-4"
            >
              <div className="text-center space-y-2 mb-4">
                <motion.h2 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight"
                >
                  Your personalised library is ready!
                </motion.h2>
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-foreground/60 text-sm"
                >
                  {!state.isForSelf 
                    ? `Here's what's waiting for ${state.profileName || 'your little one'}`
                    : "Here's what's waiting for you"
                  }
                </motion.p>
              </div>
              
              {/* Phone Frame Preview with Premium Effects */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1, 
                  y: [0, -8, 0],
                }}
                transition={{ 
                  opacity: { delay: 0.3, duration: 0.5 },
                  scale: { delay: 0.3, type: 'spring', damping: 20 },
                  y: { delay: 1.5, duration: 4, repeat: Infinity, ease: "easeInOut" }
                }}
                className="relative"
              >
                {/* Animated glow behind phone */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ 
                    opacity: [0.4, 0.7, 0.4], 
                    scale: [0.95, 1.05, 0.95],
                    rotate: [0, 5, 0, -5, 0]
                  }}
                  transition={{ delay: 0.5, duration: 6, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -inset-8 bg-gradient-to-br from-primary/50 via-purple-500/30 to-primary/50 rounded-[50px] blur-3xl -z-10"
                />
                
                <div
                  className="relative w-[280px] sm:w-[320px] aspect-[9/16] rounded-[32px] overflow-hidden"
                  style={{
                    background: 'linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--background)) 100%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 0 rgba(255, 255, 255, 0.1)',
                    border: '3px solid rgba(255, 255, 255, 0.15)',
                  }}
                >
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-b-2xl z-20" />
                  
                  {/* App Content */}
                  <div className="h-full overflow-hidden pt-8 pb-4 px-3">
                    {/* Header with Logo and Profile */}
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="flex items-center justify-between mb-4 px-1"
                    >
                      <img 
                        src="/logo.png" 
                        alt="Logo" 
                        className="h-6 object-contain"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground/70 font-medium truncate max-w-[80px]">
                          {!state.isForSelf ? (state.profileName || 'Your kid') : parentName}
                        </span>
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.7, type: 'spring', damping: 15 }}
                          className="w-8 h-8 rounded-full overflow-hidden border-2 border-primary/50"
                        >
                          <img 
                            src={(!state.isForSelf ? state.childAvatarUrl : state.avatarUrl) || '/placeholder.svg'} 
                            alt="Profile avatar" 
                            className="w-full h-full object-cover"
                          />
                        </motion.div>
                      </div>
                    </motion.div>

                    {/* Book Sections with Auto-Scrolling */}
                    <div className="space-y-4 overflow-hidden">
                      {/* Section 1: For You - Scrolls Right */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.6 }}
                      >
                        <h3 className="text-xs font-semibold text-foreground/80 mb-2 px-1">For You</h3>
                        <div className="overflow-hidden">
                          <motion.div 
                            className="flex gap-2"
                            animate={{ x: [0, -80, 0] }}
                            transition={{ 
                              delay: 1.2, 
                              duration: 8, 
                              repeat: Infinity, 
                              ease: "easeInOut" 
                            }}
                          >
                            {[...displayBooks.slice(0, 4), ...displayBooks.slice(0, 2)].map((book, index) => (
                              <motion.div
                                key={`foryou-${book.id}-${index}`}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.7 + index * 0.08 }}
                                className="flex-shrink-0 w-14 aspect-[2/3] rounded-[3px] overflow-hidden shadow-lg"
                              >
                                <img 
                                  src={book.cover_image_url || '/placeholder.svg'} 
                                  alt={book.title}
                                  className="w-full h-full object-cover"
                                />
                              </motion.div>
                            ))}
                          </motion.div>
                        </div>
                      </motion.div>

                      {/* Section 2: New Releases - Scrolls Left */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.8 }}
                      >
                        <h3 className="text-xs font-semibold text-foreground/80 mb-2 px-1">New Releases</h3>
                        <div className="overflow-hidden">
                          <motion.div 
                            className="flex gap-2"
                            animate={{ x: [-60, 0, -60] }}
                            transition={{ 
                              delay: 1.5, 
                              duration: 7, 
                              repeat: Infinity, 
                              ease: "easeInOut" 
                            }}
                          >
                            {[...remainingBooks.slice(0, 4), ...remainingBooks.slice(0, 2)].map((book, index) => (
                              <motion.div
                                key={`new-${book.id}-${index}`}
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.9 + index * 0.08 }}
                                className="flex-shrink-0 w-14 aspect-[2/3] rounded-[3px] overflow-hidden shadow-lg"
                              >
                                <img 
                                  src={book.cover_image_url || '/placeholder.svg'} 
                                  alt={book.title}
                                  className="w-full h-full object-cover"
                                />
                              </motion.div>
                            ))}
                          </motion.div>
                        </div>
                      </motion.div>

                      {/* Section 3: Continue Reading - Subtle pulse */}
                      <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 1.0 }}
                      >
                        <h3 className="text-xs font-semibold text-foreground/80 mb-2 px-1">Continue Reading</h3>
                        <div className="flex gap-2 overflow-hidden">
                          {displayBooks.slice(2, 6).map((book, index) => (
                            <motion.div
                              key={`continue-${book.id}`}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ 
                                opacity: 1, 
                                scale: [1, 1.02, 1],
                              }}
                              transition={{ 
                                opacity: { delay: 1.1 + index * 0.1 },
                                scale: { delay: 2 + index * 0.3, duration: 2, repeat: Infinity, ease: "easeInOut" }
                              }}
                              className="flex-shrink-0 w-14 aspect-[2/3] rounded-[3px] overflow-hidden shadow-lg"
                            >
                              <img 
                                src={book.cover_image_url || '/placeholder.svg'} 
                                alt={book.title}
                                className="w-full h-full object-cover"
                              />
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 }}
                className="pt-6 flex justify-center w-full"
              >
                <Button
                  onClick={handleContinue}
                  className="w-3/4 max-w-md h-14 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary/80 text-primary-foreground font-semibold text-[17px]"
                  style={{ borderRadius: '14px / 18px' }}
                >
                  Continue
                </Button>
              </motion.div>
            </motion.div>
          )}

          {/* Reader Preview Step */}
          {step === 'reader-preview' && (
            <ReaderPreviewStep 
              displayBooks={displayBooks}
              remainingBooks={remainingBooks}
              selectedBooks={state.selectedBooks}
              onContinue={handleContinue}
            />
          )}

          {/* Review Showcase Step */}
          {step === 'review-showcase' && (
            <ReviewShowcaseStep 
              displayBooks={displayBooks}
              onContinue={handleContinue}
            />
          )}

          {/* Reading Journey Step */}
          {step === 'reading-journey' && (
            <ReadingJourneyStep 
              onContinue={handleContinue}
            />
          )}
        </AnimatePresence>
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50">
      {/* Mobile: Bottom sheet style */}
      <div className="sm:hidden h-full flex flex-col justify-end">
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="min-h-[75vh] max-h-[95vh] bg-white/10 backdrop-blur-xl border-t border-white/20 rounded-t-[24px] flex flex-col overflow-hidden"
        >
          {renderContent()}
        </motion.div>
      </div>

      {/* Desktop: Centered card */}
      <div className="hidden sm:flex h-full items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-2xl max-h-[90vh] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl flex flex-col overflow-hidden relative"
        >
          {renderContent()}
        </motion.div>
      </div>
      
      {/* Payment Modal */}
      <OnboardingPaymentSheet
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={() => {
          setShowPaymentModal(false);
          navigate('/?payment_success=true');
        }}
      />
    </div>
  );
}
